import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Award, Briefcase, Network, Cpu, Calendar } from 'lucide-react';

const timelineData = [
  {
    year: '1999',
    title: 'Chip-Level Engineering Genesis',
    desc: 'Founded by Mr. Chandrakant Patel, starting as a highly specialized component and chip-level motherboard repair service in Gujarat, building a reputation for unparalleled diagnostic expertise.',
    icon: Cpu,
    color: 'from-cyan-400 to-blue-500'
  },
  {
    year: '2005',
    title: 'Telecom Partnerships & Integrations',
    desc: 'Expanded into corporate telecommunication services, managing corporate networking integrations and partnership infrastructure rollouts with leading telecom brands.',
    icon: Network,
    color: 'from-blue-500 to-indigo-500'
  },
  {
    year: '2012',
    title: 'Enterprise ICT Shift',
    desc: 'Transitioned into a comprehensive enterprise ICT system integrator. Scaled core network, storage SAN/NAS designs, firewall architectures, and server room infrastructure deployment.',
    icon: Briefcase,
    color: 'from-indigo-500 to-purple-500'
  },
  {
    year: '2018',
    title: 'Custom Vertical Operations',
    desc: 'Began delivering sector-specific solutions tailored to the strict compliance and security demands of manufacturing, healthcare, education, and finance corporate networks.',
    icon: Award,
    color: 'from-purple-500 to-rose-500'
  },
  {
    year: '2024',
    title: 'Advanced Cloud & Managed SecOps',
    desc: 'Deploying robust hybrid cloud architectures, offsite backup vaults, endpoints EDR security, and scalable desktop/laptop rental divisions.',
    icon: ShieldCheck,
    color: 'from-brand-cyan to-emerald-400'
  }
];

export default function About() {
  return (
    <section id="about" className="relative py-24 border-t border-white/5 bg-navy-dark/40 overflow-hidden">

      {/* Background radial highlight */}
      <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[60vw] h-[60vw] rounded-full bg-brand-cyan/2 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-8">

        {/* Title Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-24">
          <div className="lg:col-span-6 text-left">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-blue/10 border border-brand-blue/20 text-brand-blue text-xs font-bold uppercase tracking-widest mb-6"
            >
              <span>Our Corporate Story</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-display font-black text-4xl md:text-5xl text-white tracking-tight mb-6"
            >
              Proven IT Leadership <br />
              <span className="text-gradient">Since 1999</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="font-sans text-slate-300 leading-relaxed text-base"
            >
              Advance IT Services was established with a singular mission: to provide the highest standard of technology engineering. Led by founder <strong>Mr. Chandrakant Patel</strong>, we have grown from a micro chip-level repair lab into an enterprise-grade ICT systems and cybersecurity solutions integrator trusted by over 1,129 clients.
            </motion.p>
          </div>

          <div className="lg:col-span-6">
            {/* Mission Statement Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="p-8 rounded-3xl glass-panel relative overflow-hidden flex flex-col justify-between h-full"
            >
              {/* Soft decorative ring */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-brand-cyan/5 rounded-full blur-2xl" />

              <div>
                <span className="font-mono text-[10px] text-slate-500 font-bold uppercase tracking-widest block mb-2">
                  OUR MISSION
                </span>
                <p className="font-display font-bold text-2xl text-white leading-snug mb-8">
                  "Empowering businesses through intelligent, secure, and future-ready technology solutions."
                </p>
              </div>

              <div className="flex items-center gap-4 border-t border-white/5 pt-6 mt-4">
                <div className="w-12 h-12 rounded-full border border-brand-cyan/20 flex items-center justify-center bg-brand-cyan/5">
                  <ShieldCheck size={22} className="text-brand-cyan" />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Zero Compromise</span>
                  <span className="text-[10px] text-slate-400">Robust architectures designed for 100% security & scalability.</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Timeline Component */}
        <div className="relative max-w-4xl mx-auto mt-20">

          {/* Vertical Center Line (Desktop) */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-brand-cyan via-brand-blue to-purple-600/20 transform -translate-x-1/2 pointer-events-none" />

          {/* Timeline Nodes */}
          <div className="flex flex-col gap-12">
            {timelineData.map((node, idx) => {
              const Icon = node.icon;
              return (
                <motion.div
                  key={node.year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6 }}
                  className={`flex flex-col md:flex-row items-start md:items-center relative w-full ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                >

                  {/* Visual Node Dot on the Line */}
                  <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full border border-slate-700 bg-slate-900 flex items-center justify-center transform -translate-x-1/2 z-10 shadow-lg">
                    <div className={`w-3.5 h-3.5 rounded-full bg-gradient-to-r ${node.color} animate-pulse`} />
                  </div>

                  {/* Year Tag (Opposite to Card) */}
                  <div className={`w-full md:w-1/2 flex justify-start md:justify-end px-12 text-left md:text-right ${idx % 2 === 0 ? 'md:text-right md:pr-12 md:pl-0' : 'md:text-left md:pl-12 md:pr-0'
                    }`}>
                    <span className="font-display font-black text-4xl md:text-5xl text-slate-600/70 tracking-tight flex items-center gap-2 md:inline-flex">
                      <Calendar size={22} className="text-slate-600/40 lg:hidden" />
                      {node.year}
                    </span>
                  </div>

                  {/* Content Card (On one side of the line) */}
                  <div className={`w-full md:w-1/2 pl-12 pr-0 md:px-12 mt-4 md:mt-0 text-left`}>
                    <div className="p-6 rounded-2xl glass-panel relative glass-panel-hover overflow-hidden">
                      <div className={`absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b ${node.color}`} />

                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-brand-cyan`}>
                          <Icon size={16} />
                        </div>
                        <h4 className="font-display font-bold text-base text-white">
                          {node.title}
                        </h4>
                      </div>

                      <p className="font-sans text-xs text-slate-400 leading-relaxed">
                        {node.desc}
                      </p>
                    </div>
                  </div>

                </motion.div>
              );
            })}
          </div>

        </div>

      </div>
    </section>
  );
}
